import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase, isConfigured } from '../lib/supabase.js'

/* Live system-status widget for the admin hub. Client-side health checks for the
   site and database with latency, latency-aware states (operational / slow /
   down), a small latency sparkline, and the deployed build time + version so a
   deploy is verifiable at a glance. This is a real-time check, not historical
   uptime %. Auto-refreshes every 60s. */

// Injected by Vite (see vite.config.js); guarded for safety.
const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null
const VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null

const SLOW_MS = 1200 // above this (but reachable) → "slow" / degraded
const HISTORY = 14
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

async function timed(fn) {
  const t0 = now()
  try {
    const err = await fn()
    return { ok: !err, ms: Math.round(now() - t0), note: err || null }
  } catch (e) {
    return { ok: false, ms: null, note: e?.message || 'unreachable' }
  }
}

const checkSite = () =>
  timed(async () => {
    await fetch(`${window.location.origin}/?_hc=${Date.now()}`, { method: 'HEAD', cache: 'no-store' })
    return null
  })

const checkDb = async () => {
  if (!isConfigured) return { ok: null, ms: null, note: 'not connected' }
  return timed(async () => {
    const { error } = await supabase.from('peptides').select('id', { head: true, count: 'exact' })
    return error ? error.message : null
  })
}

// up | degraded | down | idle
const stateOf = (s) => (!s || s.ok === null ? 'idle' : !s.ok ? 'down' : s.ms > SLOW_MS ? 'degraded' : 'up')
const LABEL = { up: 'Operational', degraded: 'Slow', down: 'Down', idle: 'Not connected' }
const RANK = { down: 3, degraded: 2, up: 1, idle: 0 }

const Dot = ({ s }) => <span className={`status-dot ${s}`} aria-hidden="true" />

function Spark({ samples }) {
  if (!samples.length) return null
  const max = Math.max(120, ...samples.map((x) => x.ms || 0))
  return (
    <span className="status-spark" aria-hidden="true">
      {samples.map((x, i) => (
        <span
          key={i}
          className={`status-spark-bar ${x.state}`}
          style={{ height: `${x.ms == null ? 100 : Math.max(12, Math.round((x.ms / max) * 100))}%` }}
          title={x.ms == null ? 'down' : `${x.ms} ms`}
        />
      ))}
    </span>
  )
}

const fmtWhen = (iso) => {
  if (!iso) return null
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return iso }
}

export default function SystemStatus() {
  const [site, setSite] = useState(null)
  const [dbState, setDbState] = useState(null)
  const [checkedAt, setCheckedAt] = useState(null)
  const [busy, setBusy] = useState(false)
  const hist = useRef({ site: [], db: [] })

  const run = useCallback(async () => {
    setBusy(true)
    const [s, d] = await Promise.all([checkSite(), checkDb()])
    const push = (key, res) => {
      const arr = hist.current[key]
      arr.push({ ms: res?.ms ?? null, state: stateOf(res) })
      if (arr.length > HISTORY) arr.shift()
    }
    push('site', s)
    push('db', d)
    setSite(s)
    setDbState(d)
    setCheckedAt(new Date())
    setBusy(false)
  }, [])

  useEffect(() => {
    run()
    const id = setInterval(run, 60000)
    return () => clearInterval(id)
  }, [run])

  const rows = [
    { key: 'site', name: 'Website', state: site },
    { key: 'db', name: 'Database', state: dbState },
  ]
  // Overall = worst of the meaningful checks.
  const worst = rows.map((r) => stateOf(r.state)).reduce((a, b) => (RANK[b] > RANK[a] ? b : a), 'up')
  const overall = worst === 'down' ? { s: 'down', t: 'Service disruption' }
    : worst === 'degraded' ? { s: 'degraded', t: 'Degraded performance' }
    : { s: 'up', t: 'All systems operational' }

  return (
    <div className="card status-card">
      <div className="status-head">
        <h3 style={{ margin: 0 }}><Dot s={overall.s} /> {overall.t}</h3>
        <button className="btn btn-ghost btn-sm" onClick={run} disabled={busy}>{busy ? 'Checking…' : '↻ Recheck'}</button>
      </div>
      <ul className="status-list">
        {rows.map((r) => {
          const s = stateOf(r.state)
          return (
            <li key={r.key} className="status-row">
              <span className="status-name"><Dot s={s} /> {r.name}</span>
              <span className="status-meta">
                <Spark samples={hist.current[r.key]} />
                <span className="status-label">{LABEL[s]}</span>
                {r.state?.ms != null && <span className="status-ms">· {r.state.ms} ms</span>}
                {r.state?.note && r.state.ok === false && <span className="status-ms">· {r.state.note}</span>}
              </span>
            </li>
          )
        })}
      </ul>
      <p className="status-foot muted small">
        {checkedAt ? `Checked ${checkedAt.toLocaleTimeString()}` : 'Checking…'} · auto-refreshes every 60s
        {(VERSION || BUILD_TIME) && (
          <><br />{VERSION && `v${VERSION}`}{VERSION && BUILD_TIME && ' · '}{BUILD_TIME && `deployed ${fmtWhen(BUILD_TIME)}`}</>
        )}
      </p>
    </div>
  )
}
